import { useMemo } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import {
  EditableToken,
  useCollectionBinding,
  useContentToken,
  useProperty,
} from '@vibesites/runtime/react';
import pagesData from '../pages.json';

interface PageEntry {
  name: string;
  path: string;
  position: number;
  visible?: boolean;
  template?: string;
}

interface VirtualTour {
  virtual_tour_url?: string | null;
}

export function SitemapContent() {
  const heading = useContentToken('sitemapcontent.heading');
  const property = useProperty() as Record<string, unknown>;
  const virtualTours = useCollectionBinding<VirtualTour>('sitemapcontent.virtual_tours', { collection: 'virtual_tours' });
  const hasVirtualTours = (virtualTours || []).some((t) => t.virtual_tour_url);

  // Mirror Header / SSG: Interactive Map only when both ApartmentSync IDs exist.
  const apartmentSyncJsId = property?.custom_apartmentsync_javascript_id as string | null | undefined;
  const apartmentSyncConfigId = property?.custom_apartmentsync_config_id as string | null | undefined;
  const hasInteractiveLeasing =
    !!(apartmentSyncJsId && String(apartmentSyncJsId).trim()) &&
    !!(apartmentSyncConfigId && String(apartmentSyncConfigId).trim());

  const pages = useMemo(
    () =>
      Object.entries(pagesData as Record<string, PageEntry>)
        .filter(([alias, p]) => {
          if (p.visible === false) return false;
          if (p.template) return false;
          if (p.path === '/sitemap' || p.path.includes('{{')) return false;
          if (alias === 'virtual-tour' && !hasVirtualTours) return false;
          if (alias === 'interactive-map' && !hasInteractiveLeasing) return false;
          return true;
        })
        .map(([, p]) => p)
        .sort((a, b) => a.position - b.position),
    [hasVirtualTours, hasInteractiveLeasing],
  );

  return (
    <section className="py-16 md:py-24 bg-[var(--vibe-color-background)]">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <EditableToken
          path="sitemapcontent.heading"
          as="h1"
          className="font-heading text-3xl font-normal text-foreground mb-10 text-center"
        >
          {heading || 'Sitemap'}
        </EditableToken>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
          {pages.map((page) => (
            <a
              key={page.path}
              href={page.path}
              className="block border border-[var(--vibe-color-text)] rounded px-6 py-4 text-center font-body text-sm text-foreground hover:bg-[var(--vibe-color-primary)] hover:text-[var(--vibe-color-text-on-primary)] hover:border-[var(--vibe-color-text-on-primary)] transition-colors"
            >
              {page.name}
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'SitemapContent',
  displayName: 'Sitemap',
  tokens: {
    'sitemapcontent.heading': {
      label: 'Page Heading',
      type: 'text',
      defaultValue: 'Sitemap',
    },
  },
  collectionBindings: {
    'sitemapcontent.virtual_tours': {
      label: 'Virtual Tours',
      collection: 'virtual_tours',
    },
  },
};

(SitemapContent as any).contentSchema = contentSchema;

export default SitemapContent;
